import Uploader from '@/components/shared/uploader';
import Button from '@/components/ui/button';
import Image from '@/components/ui/images/Image';
import { useCreateOrderRefund } from '@/hooks/order/useCreateOrderRefund';
import { useReadOrder } from '@/hooks/order/useReadOrder';
import { LineItem, getLineItemsTotal } from '@/infrastructure/domain/lineItem';
import { Refund } from '@/infrastructure/domain/order';
import {
  Card,
  Collapse,
  CollapseProps,
  Divider,
  Form,
  Select,
  UploadFile,
} from 'antd';
import { useState } from 'react';
import Swal from 'sweetalert2';

interface Props {
  orderId?: string;
  lineItems: LineItem[];
  issue: string | null;
  setSelectedIssue: React.Dispatch<React.SetStateAction<string | null>>;
  onBack: () => void;
  onClose: () => void;
}

export const RefundForm: React.FC<Props> = ({
  orderId,
  lineItems,
  issue,
  setSelectedIssue,
  onBack,
  onClose,
}) => {
  const { data: order } = useReadOrder(orderId);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const createOrderRefund = useCreateOrderRefund();

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: 'Products',
      children: (
        <>
          {lineItems.map(li => (
            <div
              key={li.product.name}
              className="p-2 rounded-lg flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <Image
                  imageSource={li.product.images?.[0]}
                  className="w-12 h-12 object-cover rounded"
                />
                <div className="flex gap-4 items-center">
                  <div className="font-medium">{li.product.name}</div>
                  <div className="text-sm text-gray-500">
                    x{li.quantity} {li.variant.unit.name}
                  </div>
                </div>
              </div>
              <div>₱{Number(li.variant?.price) * Number(li.quantity)}</div>
            </div>
          ))}
        </>
      ),
    },
  ];

  const handleOnFinish = async (values: any) => {
    if (!order) {
      Swal.fire({
        icon: 'error',
        title: 'Unable to create order refund',
        text: 'Please try again later. If issue persist contact support.',
      });
      return;
    }

    try {
      const uploadedFilenames = fileList
        .map(file => file.response?.url)
        .filter(Boolean);

      const payload: Partial<Refund> = {
        cooperativeId: order?.cooperativeId,
        marketId: order?.marketId,
        lineItems,
        images: uploadedFilenames,
        reason: values.reason,
        solution: values.solution,
        description: values.description,
        refundAmount: String(getLineItemsTotal(lineItems)),
        email: order?.cooperative?.user?.email,
      };

      await createOrderRefund?.mutateAsync({
        orderId: order?.id ?? '',
        refund: payload,
      });

      Swal.fire({
        icon: 'success',
        title: 'Order refund request created',
        text: `You have successfully submitted an order refund`,
      });

      onClose();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Unable to create product',
        text: 'Please try again later. If issue persist contact support.',
      });
    }
  };

  return (
    <Form
      layout="vertical"
      className="space-y-4 pt-8"
      onFinish={handleOnFinish}
    >
      <Collapse
        items={items}
        accordion
        defaultActiveKey={lineItems.length <= 2 ? '1' : '2'}
        bordered={false}
        expandIcon={() => <></>}
      />
      <div className="grid grid-cols-2 gap-4">
        <Form.Item label="Reason" name="reason" required>
          <Select
            placeholder="Select a reason"
            onChange={value => setSelectedIssue(value)}
            value={issue}
            className="w-full"
          >
            <Select.OptGroup label="Quality Issues">
              <Select.Option value="rotten">
                Rotten or Spoiled – Perishable goods that are no longer fresh.
              </Select.Option>
              <Select.Option value="ripeness">
                Overripe or Underripe – Produce not at the desired ripeness.
              </Select.Option>
              <Select.Option value="contaminated">
                Contaminated – Signs of dirt, insects, or foreign objects.
              </Select.Option>
              <Select.Option value="off_taste">
                Off Taste or Odor – Unusual taste or smell indicating poor
                quality.
              </Select.Option>
            </Select.OptGroup>

            <Select.OptGroup label="Appearance Issues">
              <Select.Option value="visible_defects">
                Visible Defects – Bruised, broken, or deformed goods.
              </Select.Option>
              <Select.Option value="color_issues">
                Color Issues – Items not matching the expected color.
              </Select.Option>
            </Select.OptGroup>

            <Select.OptGroup label="Packaging Problems">
              <Select.Option value="unsealed">
                Unsealed Packaging – Tampered or improperly sealed packaging.
              </Select.Option>
              <Select.Option value="leaking">
                Leaking Packaging – Liquids leaking from containers.
              </Select.Option>
            </Select.OptGroup>

            <Select.OptGroup label="Incorrect Item">
              <Select.Option value="wrong_product">
                Wrong Product Delivered – Entirely different item received.
              </Select.Option>
              <Select.Option value="incorrect_variant">
                Incorrect Variant – Right product, wrong type (e.g., wrong
                flavor or variety).
              </Select.Option>
            </Select.OptGroup>

            <Select.OptGroup label="Other Issues">
              <Select.Option value="not_as_described">
                Product Not as Described – Does not match the description or
                specifications provided.
              </Select.Option>
              <Select.Option value="expired">
                Expired Product – Items delivered past their expiration date.
              </Select.Option>
              <Select.Option value="missing_products">
                Missing Products, Incomplete Order
              </Select.Option>
            </Select.OptGroup>
          </Select>
        </Form.Item>
        <Form.Item label="Solution" name="solution" required>
          {issue === 'missing_products' ? (
            <Select>
              <Select.Option value="return_and_refund">
                Return & Refund
              </Select.Option>
              <Select.Option value="return_only">Return Only</Select.Option>
              <Select.Option value="refund_only">Refund Only</Select.Option>
            </Select>
          ) : (
            <Select value="refund">
              <Select.Option value="refund">Refund</Select.Option>
            </Select>
          )}
        </Form.Item>
      </div>
      <Form.Item name="description" label="Leave your description here">
        <textarea className="w-full border rounded p-2" rows={3} />
      </Form.Item>
      <div className="col-span-2">
        <label className="text-sm font-medium mb-4 block">
          Help Us Understand the Issue by providing images
        </label>
        <div>
          <Uploader
            setFileList={setFileList}
            fileList={fileList}
            source={'reports'}
          />
        </div>
      </div>
      <Card className="flex-col mb-8">
        <div className="flex justify-between">
          <div className="text-sm text-gray-500">Refund Amount</div>
          <div className="font-bold text-xl">
            ₱{getLineItemsTotal(lineItems)}
          </div>
        </div>
        <Divider variant="dashed" plain orientationMargin={2} />
        <div className="flex justify-between">
          <div className="text-sm text-gray-500">Email</div>
          <div className="font-medium">{order?.cooperative?.user?.email}</div>
        </div>
      </Card>
      <div className="flex mt-24 gap-2 justify-end">
        <Button variant="secondary" onClick={onBack}>
          Go Back
        </Button>
        <Button htmlType="submit">Submit</Button>
      </div>
    </Form>
  );
};
